import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Buildings, ArrowRight, Sparkle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/organizations`, {
        name: orgName.trim(),
        slug: slug.trim() || undefined,
      }, { withCredentials: true });
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSlugChange = (val) => {
    setSlug(val.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40));
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8" data-testid="onboarding-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-5">
            <Buildings size={28} weight="duotone" className="text-white" />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight mb-2">Create your workspace</h1>
          <p className="text-sm text-zinc-500 leading-relaxed">
            Welcome, {user?.name}! Set up your organization to start managing projects.
          </p>
        </div>

        <form onSubmit={handleCreate} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Organization Name</Label>
            <Input
              data-testid="onboarding-org-name"
              value={orgName}
              onChange={e => { setOrgName(e.target.value); if (!slug) handleSlugChange(e.target.value); }}
              placeholder="e.g., Acme Corp"
              className="h-11 border-zinc-200"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium">Workspace URL</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400 whitespace-nowrap">inspirowork.com/</span>
              <Input
                data-testid="onboarding-slug"
                value={slug}
                onChange={e => handleSlugChange(e.target.value)}
                placeholder="acme-corp"
                className="h-11 border-zinc-200 flex-1"
              />
            </div>
          </div>

          {error && <p className="text-sm text-red-600" data-testid="onboarding-error">{error}</p>}

          <Button
            type="submit"
            data-testid="onboarding-submit"
            disabled={submitting}
            className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors duration-200"
          >
            {submitting ? 'Creating workspace...' : 'Create Workspace'}
            {!submitting && <ArrowRight className="ml-2" size={16} />}
          </Button>
        </form>

        <div className="mt-8 p-4 bg-zinc-50 rounded-lg border border-zinc-100">
          <div className="flex items-start gap-3">
            <Sparkle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" weight="fill" />
            <p className="text-xs text-zinc-500 leading-relaxed">
              Your workspace is where your team collaborates on projects. You can invite members and customize settings later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
